import ProfileIcon from 'assets/images/auth/profile.svg'
import { Field, Form, Formik, useFormikContext } from 'formik'
import useGetProfile from './hooks/useGetProfile'
import DotsLoading from 'components/loadings/dotsLoading'
import CustomButton from 'components/customButtons/primary'
import { makeSchema, initialValues } from './utilities'
import useSetAuthProfile from './hooks/useSetAuthProfile'
import { useEffect, useRef, useState } from 'react'
import ProfileModal from 'components/modals/authProfile'
import { useNavigate } from 'react-router-dom'
import posthog from 'posthog-js'
import { identifyPerson } from 'utilities/analytics'
import { POST_LOGIN_PATH } from 'utilities/navigation'

/**
 * Says so when a tap on «ورود» went nowhere.
 *
 * This screen is the last step of login, and when validation refused the
 * submit it did it in total silence — the only trace in analytics was a
 * `$rageclick` and the absence of `profile_completed`. One event turns that
 * into something we can actually watch, and into an alarm if the form ever
 * starts rejecting values it cannot show an error for again.
 */
function SubmitBlockedProbe() {
    const { submitCount, errors } = useFormikContext()
    const reported = useRef(0)
    useEffect(() => {
        if (submitCount === 0 || submitCount === reported.current) return
        // Formik validates asynchronously, so `errors` can still be empty on
        // the tick `submitCount` moves; this effect runs again when it fills.
        if (Object.keys(errors).length === 0) return
        reported.current = submitCount
        posthog.capture('profile_submit_blocked', {
            attempt: submitCount,
            fields: Object.keys(errors).join(','),
        })
    }, [submitCount, errors])
    return null
}

// The last step of every credential-based login: OTP, 2FA and admin
// impersonate all route here, so this is where three of the four login
// scenarios choose where the user lands. The fourth — mini-app auto-login —
// picks the same destination in `modules/miniapp`.
export default function ProfileLogin() {
    const [showModal, setShowModal] = useState<boolean>(false)
    const { profileData, isLoading } = useGetProfile()
    const navigate = useNavigate()
    useEffect(() => {
        if (profileData) {
            // Same person as the OTP/2FA screen identified a moment ago — but
            // the backend spells the number `+98…` and those screens spell it
            // `09…`. Going through identifyPerson() is what keeps that from
            // silently forking the person in two.
            identifyPerson(profileData?.phone_number, {
                user_id: profileData?.id,
                name: profileData?.first_name + ' ' + profileData?.last_name,
                email: profileData?.email,
                phone_number: profileData?.phone_number,
            })
            localStorage.setItem('profile', JSON.stringify(profileData))
        } else {
        }
    }, [profileData])
    const { mutate } = useSetAuthProfile(setShowModal)

    // A name that is already on the account is shown as text, not as an input.
    // These two flags decide BOTH what is rendered and what is validated, so
    // the two can never drift apart again.
    const canEditFirst = !profileData?.first_name?.length
    const canEditLast = !profileData?.last_name?.length
    return (
        <div className=" w-full h-screen bg-main-bg ">
            {isLoading ? (
                <div className=" w-full h-screen items-center">
                    <DotsLoading />
                </div>
            ) : (
                <Formik
                    enableReinitialize
                    initialValues={initialValues(profileData)}
                    validationSchema={makeSchema({
                        first: canEditFirst,
                        last: canEditLast,
                    })}
                    onSubmit={async (values) => {
                        // Send whatever the user actually changed. The old
                        // condition only saved when BOTH names were blank, so
                        // a member with just one of the two on file could type
                        // the missing half, watch it accepted, and land on the
                        // home page with it silently thrown away.
                        const payload: IAuthprofilePayload = {
                            ...(values.first_name !==
                                (profileData?.first_name ?? '') && {
                                first_name: values.first_name,
                            }),
                            ...(values.last_name !==
                                (profileData?.last_name ?? '') && {
                                last_name: values.last_name,
                            }),
                            ...(values.email !== (profileData?.email ?? '') && {
                                email: values.email,
                            }),
                        }
                        if (Object.keys(payload).length) {
                            mutate(payload)
                        } else {
                            posthog.capture('profile_completed', {
                                is_new_user: false,
                            })
                            navigate(POST_LOGIN_PATH)
                        }
                    }}
                >
                    {({ values, touched, errors }) => (
                        <Form
                            className="h-screen flex flex-col"
                            id="authProfile"
                        >
                            <SubmitBlockedProbe />
                            <p className=" text-center text-current my-6 ">
                                حساب کاربری
                            </p>
                            <div className=" mx-4  mt-24 text-center grow">
                                <div className=" bg-white shadow-[0px_2px_3px_-1px_rgba(0,0,0,0.1),0px_1px_0px_0px_rgba(25,28,33,0.02),0px_0px_0px_1px_rgba(25,28,33,0.08)] rounded-md ">
                                    <div className="relative w-full flex justify-center ">
                                        <img
                                            src={ProfileIcon}
                                            className=" mx-auto absolute -mt-14 pl-5 "
                                            alt="profile-icon"
                                        />
                                    </div>
                                    <p className=" text-text-gray text-sm my-4 pt-6">
                                        تلفن همراه
                                    </p>
                                    <p className=" mb-4">
                                        {profileData &&
                                            `0${profileData?.phone_number?.slice(
                                                3
                                            )}`}
                                    </p>
                                    <div>
                                        {canEditFirst ? (
                                            <Field
                                                type="text"
                                                className=" w-full outline-0 text-center border-t border-b border-dashed border-gray-1 py-3"
                                                placeholder="نام (الزامی)"
                                                name="first_name"
                                            />
                                        ) : (
                                            <p className=" w-full outline-0 text-center border-t border-b border-dashed border-gray-1 py-3">
                                                {profileData?.first_name}
                                            </p>
                                        )}
                                        {/* Outside the branch on purpose: an
                                            error that can only render beside an
                                            input is an error nobody sees when
                                            the value is read-only, and a form
                                            that refuses to submit while showing
                                            nothing is just a dead button. */}
                                        {touched.first_name &&
                                            errors.first_name && (
                                                <p className=" text-xs text-red-1">
                                                    {errors.first_name}
                                                </p>
                                            )}
                                        {canEditLast ? (
                                            <Field
                                                type="text"
                                                className=" w-full outline-0 text-center border-b border-dashed border-gray-1 py-3"
                                                placeholder="نام خانوادگی (الزامی)"
                                                name="last_name"
                                            />
                                        ) : (
                                            <p className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3">
                                                {profileData?.last_name}
                                            </p>
                                        )}
                                        {touched.last_name &&
                                            errors.last_name && (
                                                <p className=" text-xs text-red-1">
                                                    {errors.last_name}
                                                </p>
                                            )}
                                        {profileData &&
                                        profileData?.email &&
                                        profileData?.email.length > 0 ? (
                                            <p className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3">
                                                {profileData.email}
                                            </p>
                                        ) : (
                                            <>
                                                <Field
                                                    type="text"
                                                    className=" w-full outline-0 text-center border-b border-dashed border-gray-1 py-3"
                                                    placeholder="پست الکترونیک (اختیاری)"
                                                    name="email"
                                                />
                                                {touched.email &&
                                                    errors.email && (
                                                        <p className=" text-xs text-red-1">
                                                            {errors.email}
                                                        </p>
                                                    )}
                                            </>
                                        )}
                                        <input
                                            type="text"
                                            className=" w-full outline-0 text-center py-3"
                                            placeholder="کد معرف (اختیاری)"
                                            disabled
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className=" mb-4 mx-6">
                                <CustomButton
                                    label="ورود"
                                    type="submit"
                                    setShowModal={() => {
                                        if (
                                            profileData &&
                                            !profileData?.first_name?.length &&
                                            !profileData?.last_name?.length
                                        ) {
                                            setShowModal(true)
                                        }
                                    }}
                                />
                            </div>
                            <ProfileModal
                                show={showModal}
                                onHide={() => setShowModal(false)}
                                type="submit"
                            />
                        </Form>
                    )}
                </Formik>
            )}
        </div>
    )
}
